import {
  getCurrentPosition, getSessionUser, getSessionUserCookiesState, userIsImpersonating,
  getGoogleAnalyticsClientId, getSessionUserState,
} from '@client/selectors/helpers/session';
import { firebaseRequest, handleRequest } from '@client/listeners/lib/requests';
import {
  setAnonymousUser, updateCurrentSessionUser, startScaffoldingUser, setImpersonatingStatus,
} from '@client/slices/helpers/session.reducer';
import { trackAnalytics } from '@client/intercom';
import { getEmailHashForGravatar } from '@sda/utils/crypto';
import { getNameParts } from '@sda/utils/string';
import { showError } from '@client/listeners/lib/alerts';
import { getSelectedProfile } from '@client/selectors/helpers/profile';
import { getSanitizedVanityUrl, addToVanityUrlCacheArray } from '@sda/utils/url';
import { ProfileTypes } from '@sda/enums';
import { AnalyticsEvents } from '@sda/interfaces/queue/analytics';
import type { EnrichUserDto } from '@sda/functions/user/enrich-user.endpoint';
import type { CreateTokenDto } from '@sda/functions/system/get-token.endpoint';
import type { SignupIntents } from '@sda/enums';
import type { UserPositionDto } from '@sda/functions/dto/user-position.dto';
import type { UserLocationData } from '@sda/interfaces/location';
import type { ProviderData, User } from '@sda/interfaces/user';
import type { RootState, AppDispatch } from '@client/store';
import type { Writable } from 'type-fest';
import type { ListenerEffectAPI } from '@reduxjs/toolkit';
import type { User as AuthUser } from 'firebase/auth';

interface SendWelcomeEmailData {
  readonly email: string;
  readonly firstName: string;
  readonly providerData: ProviderData[];
}

export const sendWelcomeEmail = async (data: SendWelcomeEmailData): Promise<void> => {
  const firebase = window.Firebase;

  const { email } = data;

  try {
    await firebase.SDK.Queues.Emails().addEmail({
      substitutions: {
        userEmail: email,
      },
      template: 'ACCOUNT_SIGN_UP',
      to: email,
      sendImmediately: true,
    });
  } catch (e) {
    showError(e);
  }
};

export const enrichUser = async (listenerApi: ListenerEffectAPI<RootState, AppDispatch>): Promise<void> => {
  const state = listenerApi.getState();

  const userState = getSessionUserState(state);
  const isImpersonating = userIsImpersonating(state);

  if (userState && !isImpersonating) {
    const position = getCurrentPosition(state);
    const clientId = getGoogleAnalyticsClientId(state);

    const request = await firebaseRequest<EnrichUserDto>({
      method: 'PUT',
      url: `${process.env.API_BASE_URL}/user/enrich`,
      body: {
        ...position || {},
        clientId: clientId || null,
      },
    });

    await handleRequest(request);
  }
};

export const createUser = async (
  user: AuthUser & Partial<User> & { fullName?: string, intent: SignupIntents },
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
): Promise<void> => {
  const state = listenerApi.getState();
  const { dispatch } = listenerApi;

  const {
    uid, providerData, fullName,
    acceptsGDPR = false, acceptsTos = false, intent = null,
  } = user;

  const firebase = window.Firebase;
  const acceptsCookies = getSessionUserCookiesState(state);

  let profile: Writable<Partial<User>> = { };

  if (fullName) {
    const parts = getNameParts(fullName);

    profile = {
      ...profile,
      ...parts,
      fullName,
    };
  }

  if (providerData.length > 0) {
    const { email = null } = providerData[0];

    let cleanProviderData = [];
    cleanProviderData.push({ providerId: providerData[0].providerId, uid: providerData[0].uid });

    profile = {
      ...profile, email, providerData: cleanProviderData,
    };
  } else {
    const { email = null } = user;

    profile = {
      ...profile, email, providerData: null,
    };
  }

  const emailHash = getEmailHashForGravatar(profile.email);

  // Subscribe to any email here
  profile = {
    ...profile,
    acceptsGDPR,
    acceptsCookies,
    emailHash,
    intent,
    googleAnalyticsClientId: getGoogleAnalyticsClientId(state) || null,
    originalEmailAddress: profile.email,
    acceptsTos: acceptsTos || false,
    notifications: {
      notificationMessage: {
        email: true,
        push: true,
        sms: false,
      },
      notificationConfirmations: {
        email: true,
        push: true,
        sms: false,
      },
      notificationCancellations: {
        email: true,
        push: true,
        sms: false,
      },
      notificationArtistBeacon: {
        email: true,
        push: true,
        sms: false,
      },
      notificationPayouts: {
        email: true,
        push: true,
        sms: false,
      },
      notificationTicketManifest: {
        email: false,
        push: false,
        sms: false,
      },
      notificationReview: {
        email: true,
        push: true,
        sms: false,
      },
      notificationAccount: {
        email: true,
        push: true,
        sms: false,
      },
      notificationSales: {
        email: true,
        push: true,
        sms: false,
      },
      notificationShowCalls: {
        email: true,
        push: true,
        sms: false,
      },
      notificationUpcomingShows: {
        email: true,
        push: true,
        sms: false,
      },
    },
  };

  await firebase.helpers.runTransaction(async () => {
    await firebase.SDK.Users.set(uid, firebase.SDK.Users.filter(profile, firebase.SDK.Users.PublicMap));
    await firebase.SDK.Users.Private().set(uid, firebase.SDK.Users.Private().filter(profile, firebase.SDK.Users.PrivateMap));
  });

  dispatch(updateCurrentSessionUser({ ...profile, id: uid }));
  dispatch(startScaffoldingUser(firebase.auth.currentUser));

  listenerApi.fork(async () => {
    await enrichUser(listenerApi);
  });

  /**
   * Send create account track event
   * - sending intent allows for filtering based on
   * - account creation with intent ex. 'invite'
   * Note that email, firstName, and lastName may all be null if it's a social login
   */
  const data = {
    email: profile.email || null,
    firstName: profile.firstName || null,
    lastName: profile.lastName || null,
    providerActual: profile.providerData ? profile.providerData[0].providerId : null,
    intentActual: profile.intent,
  };

  trackAnalytics(AnalyticsEvents.CREATED_ACCOUNT, data);

  // Send a welcome email
  listenerApi.fork(async () => {
    await sendWelcomeEmail({ ...data, providerData: profile.providerData });
  });
};

export const generateAnonymousUserLocationHash = async (listenerApi: ListenerEffectAPI<RootState, AppDispatch>): Promise<void> => {
  const state = listenerApi.getState();
  const { dispatch } = listenerApi;

  const position = getCurrentPosition(state);

  const request = await firebaseRequest<UserPositionDto | object>({
    url: `${process.env.API_BASE_URL}/system/generateUserHash`,
    method: 'POST',
    body: position || {},
  });

  const response = await handleRequest<UserLocationData>(request);

  const locationData = {
    locationHash: response.data.locationHash,
  };

  dispatch(setAnonymousUser(locationData));
};

export const getVanityUrl = async (
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
  updatedName: string,
) => {
  const state = listenerApi.getState();
  const firebase = window.Firebase;

  const profile = getSelectedProfile(state);

  // if the name changed, update the vanityUrl
  if (profile.name !== updatedName) {
    console.log('updating vanityUrl for name change');

    let vanityUrl: string;

    const vanityUrlFromName = getSanitizedVanityUrl(updatedName);
    let vanityUrlLowercase = vanityUrlFromName.toLowerCase();

    const endpoint = profile.profileType === ProfileTypes.ACT ? firebase.SDK.Acts : firebase.SDK.Venues;

    const existing = await endpoint.getBy((query) => query.isEqualTo('vanityUrlLowercase', vanityUrlLowercase));

    if (!existing) {
      console.log(`no artist with vanityUrl found for ${vanityUrlFromName}... using it as is.`);
      // vanity with name doesn't exist, use it directly
      vanityUrl = vanityUrlFromName;
    } else {
      console.log(`found match for vanityUrl ${vanityUrl}`);

      let total = 1;

      const homophoneProfiles = await endpoint.listBy((query) => [
        query.isGreaterThanOrEqualTo('vanityUrlLowercase', `${vanityUrlLowercase}-1`),
        query.isLessThanOrEqualTo('vanityUrlLowercase', `${vanityUrlLowercase}-${Number.MAX_SAFE_INTEGER}`),
      ]);

      if (homophoneProfiles) {
        total += Object.keys(homophoneProfiles).length;
      }

      // vanity with name already exists, create an alternate appending the last available number
      vanityUrl = `${vanityUrlFromName}-${total}`;

      console.log(`using vanityUrl with count as ${vanityUrl}`);
    }

    vanityUrlLowercase = vanityUrl.toLowerCase();
    const vanityUrlCache = addToVanityUrlCacheArray(profile.vanityUrlCache, vanityUrlLowercase);

    return {
      vanityUrl,
      vanityUrlCache,
      vanityUrlLowercase,
    };
  }

  return { };
};

export const fetchFirebaseToken = async (listenerApi: ListenerEffectAPI<RootState, AppDispatch>): Promise<void> => {
  const state = listenerApi.getState();
  const { dispatch } = listenerApi;

  const firebase = window.Firebase;

  const sessionUser = getSessionUser(state);
  let userId = null;

  if (!sessionUser) {
    userId = firebase.auth.currentUser.uid;
  } else {
    userId = sessionUser.uid;
  }

  const request = await firebaseRequest<CreateTokenDto>({
    url: `${process.env.API_BASE_URL}/system/token`,
    method: 'POST',
    body: {
      userId,
    },
  });

  const response = await handleRequest<void>(request);

  if (response.status === true) {
    await firebase.auth.currentUser.getIdToken(true);

    // If this is an impersonation, close the modal
    // The firebase token being refreshed is when the impersonation is finalized
    // This status refers to the Toggling Impersonation _modal_, not the actual impersonation state
    dispatch(setImpersonatingStatus(false));
  } else {
    showError(response.error);
  }
};
